import { useState } from "react";
import { DashboardLayout } from "../components/dashboard/dashboard-layout";
import { PageHeader } from "../components/dashboard/page-header";
import { 
  FileText, 
  Download, 
  Plus, 
  Clock, 
  Calendar, 
  BarChart2, 
  PieChart, 
  LineChart, 
  Search,
  Filter,
  MoreHorizontal,
  ArrowUpRight
} from "lucide-react";

export default function ReportsPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  
  const reports = [
    { 
      id: 1, 
      title: "Monthly Revenue Report", 
      description: "Detailed breakdown of revenue sources and trends",
      category: "finance",
      type: "bar",
      lastUpdated: "2 hours ago",
      schedule: "Monthly",
      status: "Generated"
    },
    { 
      id: 2, 
      title: "Customer Acquisition", 
      description: "Analysis of customer acquisition channels and costs",
      category: "marketing",
      type: "line",
      lastUpdated: "1 day ago",
      schedule: "Weekly",
      status: "Generated"
    },
    { 
      id: 3, 
      title: "Product Performance", 
      description: "Performance metrics for all products",
      category: "product",
      type: "pie",
      lastUpdated: "3 days ago",
      schedule: "Monthly",
      status: "Generated"
    },
    { 
      id: 4, 
      title: "Website Traffic Analysis", 
      description: "Detailed traffic sources and user behavior",
      category: "marketing",
      type: "line",
      lastUpdated: "1 week ago",
      schedule: "Weekly",
      status: "Generated"
    },
    { 
      id: 5, 
      title: "Quarterly Financial Summary", 
      description: "Summary of financial performance for Q2 2025",
      category: "finance",
      type: "bar",
      lastUpdated: "2 weeks ago",
      schedule: "Quarterly",
      status: "Generated"
    },
    { 
      id: 6, 
      title: "Customer Satisfaction Survey", 
      description: "Results from the latest customer satisfaction survey",
      category: "customer",
      type: "pie",
      lastUpdated: "1 month ago",
      schedule: "Quarterly",
      status: "Generated"
    },
  ];
  
  const filteredReports = reports.filter(report => {
    const matchesSearch = report.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          report.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "all" || report.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });
  
  const getReportIcon = (type: string) => {
    switch (type) {
      case "bar":
        return <BarChart2 className="h-5 w-5 text-gray-500 dark:text-gray-400" />;
      case "pie":
        return <PieChart className="h-5 w-5 text-gray-500 dark:text-gray-400" />;
      case "line":
        return <LineChart className="h-5 w-5 text-gray-500 dark:text-gray-400" />;
      default:
        return <FileText className="h-5 w-5 text-gray-500 dark:text-gray-400" />;
    }
  };
  
  const getCategoryColor = (category: string) => {
    switch (category) {
      case "finance":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300";
      case "marketing":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300";
      case "product":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
      case "customer":
        return "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
    }
  };
  
  return (
    <DashboardLayout>
      <PageHeader 
        title="Reports" 
        description="Generate and manage your business reports"
      />
      
      {/* Stats Cards */}
      <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-3 mb-6">
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 p-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Reports</h3>
          </div>
          <div className="flex items-baseline">
            <span className="text-3xl font-bold">24</span>
            <span className="ml-2 text-sm font-medium flex items-center text-green-600 dark:text-green-400">
              <ArrowUpRight className="h-3 w-3 mr-1" />
              +3
            </span>
          </div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Added this month</div>
        </div>
        
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 p-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Scheduled Reports</h3>
          </div>
          <div className="flex items-baseline">
            <span className="text-3xl font-bold">18</span>
          </div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Auto-generated on schedule</div>
        </div>
        
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 p-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Recent Downloads</h3>
          </div>
          <div className="flex items-baseline">
            <span className="text-3xl font-bold">42</span>
          </div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">In the last 30 days</div>
        </div>
      </div>
      
      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-6">
        <div className="relative w-full sm:w-64 md:w-80">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="search"
            placeholder="Search reports..."
            className="pl-10 pr-4 py-2 w-full rounded-lg bg-white dark:bg-gray-900 text-sm transition-all duration-300 focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-gray-700 border border-gray-200 dark:border-gray-800"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <select 
            className="px-3 py-2 rounded-lg bg-white dark:bg-gray-900 text-sm border border-gray-200 dark:border-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-gray-700"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="all">All Categories</option>
            <option value="finance">Finance</option>
            <option value="marketing">Marketing</option>
            <option value="product">Product</option>
            <option value="customer">Customer</option>
          </select>
          
          <button className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200">
            <Filter className="h-4 w-4" />
            <span className="hidden sm:inline">Filter</span>
          </button>
          
          <button className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-white transition-all duration-200">
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">New Report</span>
          </button>
        </div>
      </div>
      
      {/* Reports Grid */}
      <div className="grid gap-4 sm:gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {filteredReports.map((report) => (
          <div 
            key={report.id} 
            className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 hover:shadow-md transition-all duration-200 hover:-translate-y-1"
          >
            <div className="p-5">
              <div className="flex items-start justify-between mb-4">
                <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-800">
                  {getReportIcon(report.type)}
                </div>
                <button className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                  <MoreHorizontal className="h-5 w-5" />
                </button>
              </div>
              
              <h3 className="text-lg font-semibold mb-2">{report.title}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{report.description}</p>
              
              <div className="flex items-center gap-2 mb-3">
                <span className={`text-xs px-2 py-1 rounded-full ${getCategoryColor(report.category)}`}>
                  {report.category.charAt(0).toUpperCase() + report.category.slice(1)}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                  <Clock className="h-3 w-3 mr-1" />
                  {report.lastUpdated}
                </span>
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-800">
                <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                  <Calendar className="h-3 w-3 mr-1" />
                  {report.schedule}
                </span>
                <button className="flex items-center gap-1 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-all duration-200">
                  <Download className="h-4 w-4" />
                  <span>Download</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}